import { useState, useCallback } from 'react';
import { ImageSource, BackgroundImage } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';

const DEFAULT_COLORS = ['#2D3436', '#1B2838', '#2C3E50', '#8B1A1A', '#636E72', '#1A535C', '#4A1942', '#722F37'];

export function useImageSourceConfig(workspaceId: string | undefined) {
  const [enabledImageSources, setEnabledImageSources] = useState<ImageSource[]>(['stock', 'pastel']);
  const [imageMix, setImageMix] = useState({ stock: 60, pastel: 40, asset: 0, ai: 0 });
  const [backgroundImages, setBackgroundImages] = useState<BackgroundImage[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [customColors, setCustomColors] = useState<string[]>(DEFAULT_COLORS);
  const [colorPickerValue, setColorPickerValue] = useState('#3498db');

  const fetchBackgroundImages = async () => {
    try {
      const data = await apiClient.get<BackgroundImage[]>(`/api/workspaces/${workspaceId}/background-images`);
      setBackgroundImages(data);
    } catch (error) {
      console.error('Failed to fetch background images:', error);
    }
  };

  const toggleImageSource = useCallback((source: ImageSource) => {
    setEnabledImageSources((prev) => {
      const isEnabled = prev.includes(source);
      let newSources: ImageSource[];

      if (isEnabled) {
        if (prev.length === 1) return prev;
        newSources = prev.filter((s) => s !== source);
      } else {
        newSources = [...prev, source];
      }

      const evenSplit = Math.floor(100 / newSources.length);
      const remainder = 100 - evenSplit * newSources.length;

      setImageMix({
        stock: newSources.includes('stock') ? evenSplit + (newSources[0] === 'stock' ? remainder : 0) : 0,
        pastel: newSources.includes('pastel') ? evenSplit + (newSources[0] === 'pastel' ? remainder : 0) : 0,
        asset: newSources.includes('asset') ? evenSplit + (newSources[0] === 'asset' ? remainder : 0) : 0,
        ai: newSources.includes('ai') ? evenSplit + (newSources[0] === 'ai' ? remainder : 0) : 0,
      });

      return newSources;
    });
  }, []);

  const updateImageMix = useCallback((source: ImageSource, value: number) => {
    setImageMix((prev) => {
      const newMix = { ...prev, [source]: value };
      const total = Object.values(newMix).reduce((sum, v) => sum + v, 0);
      if (total !== 100 && total > 0) {
        const otherSources = enabledImageSources.filter((s) => s !== source);
        const otherTotal = otherSources.reduce((sum, s) => sum + newMix[s], 0);
        const targetOther = 100 - value;

        if (otherTotal > 0) {
          otherSources.forEach((s) => {
            newMix[s] = Math.round((newMix[s] / otherTotal) * targetOther);
          });
        }
      }
      return newMix;
    });
  }, [enabledImageSources]);

  const toggleAssetSelection = useCallback((assetId: string) => {
    setSelectedAssetIds((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  }, []);

  const selectAllAssets = useCallback(() => {
    setSelectedAssetIds(backgroundImages.map((img) => img.id));
  }, [backgroundImages]);

  const deselectAllAssets = useCallback(() => {
    setSelectedAssetIds([]);
  }, []);

  const addCustomColor = useCallback(() => {
    if (!customColors.includes(colorPickerValue)) {
      setCustomColors((prev) => [...prev, colorPickerValue]);
    }
  }, [colorPickerValue, customColors]);

  const removeCustomColor = useCallback((index: number) => {
    setCustomColors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllColors = useCallback(() => {
    setCustomColors([]);
  }, []);

  return {
    enabledImageSources,
    imageMix,
    backgroundImages,
    selectedAssetIds,
    customColors,
    colorPickerValue,
    setColorPickerValue,
    fetchBackgroundImages,
    toggleImageSource,
    updateImageMix,
    toggleAssetSelection,
    selectAllAssets,
    deselectAllAssets,
    addCustomColor,
    removeCustomColor,
    clearAllColors,
  };
}
